import type { ImageAssetFragment } from "@commerce-frontend/types";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Button, type Props as ButtonProps } from "@commerce-frontend/ui/primitives/Button/Button";
import { cva } from "class-variance-authority";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { ReactElement } from "react";

type Props = {
	image: ImageAssetFragment;
	selected: boolean;
	index: number;
	available?: boolean;
} & ButtonProps;

// TODO: Fix image sizes, make more reusable by abstracting the button away to a component
export const Thumbnail = ({
	image,
	selected,
	disabled,
	index,
	className,
	available = true,
	...props
}: Props): ReactElement => {
	const t = useTranslations("Thumbnail");
	return (
		<Button
			{...props}
			disabled={disabled}
			className={cn([
				buttonVariants({
					selected,
					disabled,
				}),
				className,
			])}
			aria-label={t("view-image", { count: index + 1 })}
		>
			<Image
				width="80"
				height="96"
				alt={image.alt ?? ""}
				className={cn("size-full object-contain aspect-[4/5]", disabled && "grayscale")}
				sizes="80px"
				src={image.filename}
			/>
			<div
				className={cn(
					"absolute left-0 top-0 z-40 size-full transition-all",
					selected && "border-t-4 border-orange-dark-200 border-b-transparent",
					!disabled &&
						"group-hover:border-t-4 group-hover:border-orange-dark-200 group-hover:border-b-transparent",
				)}
			/>
			<div
				className={cn([
					"absolute left-0 top-0 z-20 size-full transition-all",
					(disabled || !available) && " bg-gray-50/50",
					!available &&
						"before:absolute before:left-0 before:top-0 before:size-full before:bg-strikethrough before:content-['']",
				])}
			/>
		</Button>
	);
};

const buttonVariants = cva(
	"group relative h-24 w-20 overflow-hidden rounded-xxs bg-gray-50 transition-all",
	{
		variants: {
			selected: {
				true: "pointer-events-none",
				false: "",
			},
			disabled: {
				true: "pointer-events-none border shadow-none",
				false: "",
			},
		},
		defaultVariants: {
			selected: false,
			disabled: false,
		},
		compoundVariants: [
			{
				selected: false,
				disabled: false,
				className: "hover:shadow-2",
			},
			{
				disabled: false,
				selected: true,
				className: "shadow-0",
			},
			{
				disabled: false,
				selected: false,
				className: "shadow-1",
			},
		],
	},
);
